import * as Phaser from "phaser";
import * as twaSDK from "@telegram-apps/sdk";
import { KeysManagerGlobalPlugin } from "./KeysManagerGlobalPlugin.ts";
import { SettingsGlobalPlugin } from "./SettingsGlobalPlugin.ts";
import { SoundManagerGlobalPlugin } from "./SoundManagerGlobalPlugin.ts";

export class TelegramGlobalPlugin extends Phaser.Plugins.BasePlugin {
  public twaSDK = twaSDK;
  public TWA = window.Telegram.WebApp;
  public WEBVIEW = window.Telegram.WebView;

  public VIEWPORT_HEIGHT = this.TWA?.viewportStableHeight ||
    this.TWA?.viewportHeight;
  public LANG = this.TWA.initDataUnsafe.user.language_code === "ru"
    ? "ru"
    : "en" as "ru" | "en";

  public REVIVE_ADS_CONTROLLER: any = null;
  public RESTORE_STAT_ADS_CONTROLLER: any = null;
  public COINS_ADS_CONTROLLER: any = null;
  public CONTINUE_GAME_ADS_CONTROLLER: any = null;
  public GAME_OVER_ADS_CONTROLLER: any = null;

  public LAST_AD_SHOWN_TIME: Date | null = null;

  public isAdLoading: boolean = false;
  public reviveButtonText: Phaser.GameObjects.Text | null = null; // For referencing the revive button text if needed

  constructor(pluginManager: Phaser.Plugins.PluginManager) {
    super(pluginManager);

    // Listen for AdsGram script load events
    window.addEventListener("adsgram-loaded", () => {
      console.log("AdsGram loaded, initializing controllers");
      this.initAdController();
    });

    window.addEventListener("adsgram-error", (event) => {
      const customEvent = event as CustomEvent;
      console.warn(
        "AdsGram failed to load, ads will be disabled:",
        customEvent.detail,
      );
    });

    // Try to initialize if AdsGram is already available (in case script loads very fast)
    if ((window as any).Adsgram) {
      this.initAdController();
    }
  }

  private initAdController(): void {
    const reviveBlockId = (window as any).tunerTwaConfig?.game?.ads
      ?.reviveAdBlockId;
    const restoreStatBlockId = (window as any).tunerTwaConfig?.game?.ads
      ?.restoreStatAdBlockId;
    const coinsBlockId = (window as any).tunerTwaConfig?.game?.ads
      ?.coinsAdBlockId;
    const continueGameBlockId = (window as any).tunerTwaConfig?.game?.ads
      ?.catchFoodReviveAdBlockId;
    const gameOverBlockId = (window as any).tunerTwaConfig?.game?.ads
      ?.catchFoodGameOverAdBlockId;

    if (!this.REVIVE_ADS_CONTROLLER) {
      try {
        this.REVIVE_ADS_CONTROLLER = (window as any).Adsgram.init({
          blockId: reviveBlockId,
        });
      } catch (error) {
        console.error("Error initializing AdsGram controller:", error);
      }
    }

    if (!this.RESTORE_STAT_ADS_CONTROLLER) {
      try {
        this.RESTORE_STAT_ADS_CONTROLLER = (window as any).Adsgram.init({
          blockId: restoreStatBlockId,
        });
      } catch (error) {
        console.error("Error initializing AdsGram controller:", error);
      }
    }

    if (!this.COINS_ADS_CONTROLLER) {
      try {
        this.COINS_ADS_CONTROLLER = (window as any).Adsgram.init({
          blockId: coinsBlockId,
        });
      } catch (error) {
        console.error("Error initializing AdsGram controller:", error);
      }
    }
    if (!this.CONTINUE_GAME_ADS_CONTROLLER) {
      try {
        this.CONTINUE_GAME_ADS_CONTROLLER = (window as any).Adsgram.init({
          blockId: continueGameBlockId,
        });
      } catch (error) {
        console.error("Error initializing AdsGram controller:", error);
      }
    }
    if (!this.GAME_OVER_ADS_CONTROLLER) {
      try {
        this.GAME_OVER_ADS_CONTROLLER = (window as any).Adsgram.init({
          blockId: gameOverBlockId,
        });
      } catch (error) {
        console.error("Error initializing AdsGram controller:", error);
      }
    }
  }

  public async showAd(
    type: "revive" | "restoreStat" | "coins" | "continueGame" | "gameOver",
  ): Promise<boolean> {
    // Check if AdsGram is available
    if (!(window as any).Adsgram) {
      console.warn("AdsGram not loaded, cannot show ads");
      return false;
    }

    const controller = type === "revive"
      ? this.REVIVE_ADS_CONTROLLER
      : type === "restoreStat"
      ? this.RESTORE_STAT_ADS_CONTROLLER
      : type === "continueGame"
      ? this.CONTINUE_GAME_ADS_CONTROLLER
      : type === "gameOver"
      ? this.GAME_OVER_ADS_CONTROLLER
      : this.COINS_ADS_CONTROLLER;
    if (!controller) {
      console.error("Ad controller not initialized");
      return false;
    }

    console.log(type);
    console.log(this.LAST_AD_SHOWN_TIME);

    if (
      type === "gameOver" && this.LAST_AD_SHOWN_TIME &&
      (Date.now() - this.LAST_AD_SHOWN_TIME.getTime() < 2 * 60 * 1000)
    ) {
      console.log(true);
      return false;
    }

    if (type === 'gameOver' && !this.LAST_AD_SHOWN_TIME){
      this.LAST_AD_SHOWN_TIME = new Date(1980, 0, 1);
      return false
    }

    if (type==='continueGame' || type==='gameOver'){
      this.LAST_AD_SHOWN_TIME = new Date();
    }

    const soundManager = this.game.plugins.get(
      "soundManager",
    ) as SoundManagerGlobalPlugin;
    const settingsManager = this.game.plugins.get(
      "settings",
    ) as SettingsGlobalPlugin;
    let wasMusicEnabled = false;
    if (settingsManager && typeof settingsManager.getSetting === "function") {
      wasMusicEnabled = settingsManager.getSetting("isMusicEnabled");
    }
    if (soundManager) {
      soundManager.stopMusic();
    }

    const keys = this.game.plugins.get(
      "keysManager",
    ) as KeysManagerGlobalPlugin;

    try {
      this.isAdLoading = true;
      const result = await controller.show();

      if (result && result.done) {
        console.log(result);
        if (wasMusicEnabled && soundManager) {
          soundManager.playMusic(keys.assets.sounds.music.home);
        }
        this.isAdLoading = false;
        return true;
      } else {
        console.log("Ad was not watched to completion");
        if (wasMusicEnabled && soundManager) {
          soundManager.playMusic(keys.assets.sounds.music.home);
        }
        this.isAdLoading = false;
        return false;
      }
    } catch (error) {
      console.error("Error showing ad:", error);
      if (wasMusicEnabled && soundManager) {
        soundManager.playMusic(keys.assets.sounds.music.home);
      }
      this.isAdLoading = false;
      return false;
    }
  }
}
